/**
 * Campaigns Service
 * CRUD operations and management for marketing campaigns and flights
 */

import { supabase } from "./supabase";
import type {
  Campaign,
  CampaignFlight,
  CampaignPerformance,
  CreateCampaignDTO,
  UpdateCampaignDTO,
  CreateFlightDTO,
  UpdateFlightDTO,
  SMSTemplate,
  CreateSMSTemplateDTO,
  UpdateSMSTemplateDTO,
} from "../types/campaign.types";
import type { UUID } from "../types/common.types";
import { visibleCampaigns } from "../utils/campaignSoftDelete";
import { buildDuplicateFlightDTO } from "../utils/flightDuplicate";
import { sanitizePostgrestSearchTerm } from "../utils/postgrestSearch";

// ========================================
// CONSTANTS
// ========================================

export const CAMPAIGN_STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

export const FLIGHT_STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "scheduled", label: "Scheduled" },
  { value: "sending", label: "Sending" },
  { value: "sent", label: "Sent" },
  { value: "cancelled", label: "Cancelled" },
  { value: "failed", label: "Failed" },
];

// ========================================
// CAMPAIGN OPERATIONS
// ========================================

/**
 * Get all campaigns
 */
export async function getCampaigns(): Promise<CampaignListRow[]> {
  // Soft-deleted campaigns (migration 203) are hidden. `not.is.true` keeps NULL (= not deleted) rows.
  // flight statuses come along so the list can tell an unsent campaign (deletable) from a sent one.
  const { data, error } = await supabase
    .from("campaigns")
    .select("*, campaign_flights(status)")
    .not("is_deleted", "is", true)
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching campaigns:", error);
    throw new Error(`Failed to fetch campaigns: ${error.message}`);
  }

  return toCampaignListRows(data);
}

/** A campaign as the list shows it: the row plus its flights' statuses (never written back). */
export type CampaignListRow = Campaign & { flight_statuses: string[] };

function toCampaignListRows(data: unknown): CampaignListRow[] {
  return visibleCampaigns(
    (
      (data ?? []) as Array<
        Campaign & { campaign_flights?: Array<{ status: string }> | null }
      >
    ).map(({ campaign_flights, ...c }) => ({
      ...(c as Campaign),
      flight_statuses: (campaign_flights ?? []).map((f) => f.status),
    })),
  );
}

/**
 * Get a single campaign by ID
 */
export async function getCampaign(id: UUID): Promise<Campaign | null> {
  const { data, error } = await supabase
    .from("campaigns")
    .select("*")
    .eq("id", id)
    .single();

  if (error) {
    if (error.code === "PGRST116") {
      return null; // Not found
    }
    console.error("Error fetching campaign:", error);
    throw new Error(`Failed to fetch campaign: ${error.message}`);
  }

  return data as Campaign;
}

/**
 * Create a new campaign
 */
export async function createCampaign(
  campaign: CreateCampaignDTO,
): Promise<Campaign> {
  const { data, error } = await supabase
    .from("campaigns")
    .insert([
      {
        ...campaign,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
    ])
    .select()
    .single();

  if (error) {
    console.error("Error creating campaign:", error);
    throw new Error(`Failed to create campaign: ${error.message}`);
  }

  return data as Campaign;
}

/**
 * Update a campaign
 */
export async function updateCampaign(
  id: UUID,
  updates: UpdateCampaignDTO,
): Promise<Campaign> {
  const { data, error } = await supabase
    .from("campaigns")
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
    })
    .eq("id", id)
    .select()
    .single();

  if (error) {
    console.error("Error updating campaign:", error);
    throw new Error(`Failed to update campaign: ${error.message}`);
  }

  return data as Campaign;
}

/**
 * Soft-delete (hide) an unsent campaign. Admin/superadmin only; the database refuses anything else with a named
 * error (CAMPAIGN_DELETE_ADMIN_ONLY / CAMPAIGN_DELETE_ALREADY_SENT / CAMPAIGN_DELETE_EIB_INVITATION /
 * CAMPAIGN_NOT_FOUND) — map it with campaignDeleteErrorMessage(). Idempotent. History is kept; there is no
 * client hard delete any more (migration 203 removed the DELETE policy and grant).
 */
export async function softDeleteCampaign(id: UUID): Promise<void> {
  const { error } = await supabase.rpc("soft_delete_campaign", {
    p_campaign_id: id,
  });
  if (error) {
    console.error("Error deleting campaign:", error);
    throw new Error(error.message);
  }
}

/**
 * Get campaign performance metrics
 */
export async function getCampaignPerformance(
  campaignId: UUID,
): Promise<CampaignPerformance | null> {
  const { data, error } = await supabase
    .from("campaign_performance")
    .select("*")
    .eq("campaign_id", campaignId)
    .single();

  if (error) {
    if (error.code === "PGRST116") {
      return null; // Not found
    }
    console.error("Error fetching campaign performance:", error);
    throw new Error(`Failed to fetch campaign performance: ${error.message}`);
  }

  return data as CampaignPerformance;
}

/**
 * Get all campaign performance metrics
 */
export async function getAllCampaignPerformance(): Promise<
  CampaignPerformance[]
> {
  const { data, error } = await supabase
    .from("campaign_performance")
    .select("*")
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching campaign performance:", error);
    throw new Error(`Failed to fetch campaign performance: ${error.message}`);
  }

  return data as CampaignPerformance[];
}

// ========================================
// CAMPAIGN OFFERS
// ========================================

/**
 * Get offers associated with a campaign via campaign_offers junction table
 */
export async function getCampaignOffers(campaignId: UUID): Promise<
  Array<{
    id: string;
    offer_id: string;
    priority: number;
    offer: {
      id: string;
      name: string;
      offer_type: string;
      value_amount: number | null;
      is_active: boolean;
    };
  }>
> {
  const { data, error } = await supabase
    .from("campaign_offers")
    .select(
      "id, offer_id, priority, offers(id, name, offer_type, value_amount, is_active)",
    )
    .eq("campaign_id", campaignId)
    .order("priority", { ascending: true });

  if (error) {
    console.error("Error fetching campaign offers:", error);
    return [];
  }

  // Flatten the joined offer data
  return (data || []).map((co: any) => ({
    id: co.id,
    offer_id: co.offer_id,
    priority: co.priority,
    offer: co.offers,
  }));
}

// ========================================
// FLIGHT OPERATIONS
// ========================================

/**
 * Get flights for a campaign
 */
export async function getCampaignFlights(
  campaignId: UUID,
): Promise<CampaignFlight[]> {
  const { data, error } = await supabase
    .from("campaign_flights")
    .select("*")
    .eq("campaign_id", campaignId)
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching campaign flights:", error);
    throw new Error(`Failed to fetch campaign flights: ${error.message}`);
  }

  return data as CampaignFlight[];
}

/**
 * Count reusable vouchers per flight for a small set of flight ids (a campaign's
 * flights). Used to qualify reminder source-flight candidates whose `generate_vouchers`
 * flag is stale (false/null) but which DID mint vouchers. Uses the grouped
 * `get_flight_voucher_counts` RPC so a campaign with thousands of vouchers returns one
 * row per flight (not one per voucher). Best-effort: on error returns {} and the caller
 * falls back to the generate_vouchers flag.
 */
export async function getReusableVoucherCountsByFlight(
  flightIds: UUID[],
): Promise<Record<string, number>> {
  const ids = flightIds.filter(Boolean);
  if (ids.length === 0) {
    return {};
  }
  const { data, error } = await supabase.rpc("get_flight_voucher_counts", {
    p_flight_ids: ids,
  });
  if (error) {
    console.error("Error counting reusable vouchers by flight:", error);
    return {};
  }
  const counts: Record<string, number> = {};
  for (const row of (data as { flight_id: string; voucher_count: number }[]) ||
    []) {
    if (row.flight_id) {
      counts[row.flight_id] = Number(row.voucher_count) || 0;
    }
  }
  return counts;
}

/**
 * Get a single flight by ID, with the joined audience-list relation hydrated.
 *
 * The joined `list` field is present (possibly null) on every successful
 * fetch. Callers should distinguish:
 *   - flight.list_id present + list is null  → audience was deleted (warn)
 *   - flight.list_id present + list populated → audience valid
 *   - flight.list_id absent                   → no audience configured
 *
 * Before this change, the call used `select('*')` which doesn't hydrate
 * the FK relation, so the `list` field was always undefined. The
 * FlightDetail page's "Audience list deleted" warning treated that as
 * "deleted" and fired even for healthy flights.
 */
export async function getFlight(
  id: UUID,
): Promise<CampaignFlightWithList | null> {
  const { data, error } = await supabase
    .from("campaign_flights")
    .select(
      // campaign(...) carries the reporting category so the flight page can show the categories
      // this flight will actually send with, without a second round trip.
      "*, list:lists(id, name, is_dynamic, contact_count, last_calculated), " +
        "campaign:campaigns(id, name, sendgrid_category)",
    )
    .eq("id", id)
    .single();

  if (error) {
    if (error.code === "PGRST116") {
      return null; // Not found
    }
    console.error("Error fetching flight:", error);
    throw new Error(`Failed to fetch flight: ${error.message}`);
  }

  return data as unknown as CampaignFlightWithList;
}

/**
 * Shape returned by getFlight() — extends the base CampaignFlight type
 * with the hydrated audience-list relation. Kept narrow on purpose: we
 * only need the fields the UI consumes (name for display + ids/counts
 * for context). Don't widen without weighing the row size cost when
 * many flights are listed.
 */
export interface CampaignFlightWithList extends CampaignFlight {
  list: {
    id: UUID;
    name: string;
    is_dynamic: boolean;
    contact_count: number | null;
    last_calculated: string | null;
  } | null;
}

export interface FlightDeferralSummary {
  total_deferred: number;
  top_reasons: { reason: string; count: number }[];
  top_providers: { provider: string; count: number }[];
}

/**
 * Aggregate of a flight's CURRENTLY deferred recipients (throttled by the receiving provider,
 * no delivered-or-bounced result yet) — top reasons + provider concentration. Powers the
 * FlightDetail deferral panel so an operator can see a "provider is throttling us" pile-up
 * (an infra/deliverability matter) rather than an opaque "pending". Returns aggregates only,
 * never individual addresses.
 */
export async function getFlightDeferralSummary(
  flightId: UUID,
): Promise<FlightDeferralSummary | null> {
  const { data, error } = await supabase.rpc("get_flight_deferral_summary", {
    p_flight_id: flightId,
  });
  if (error) {
    console.error("Error fetching flight deferral summary:", error);
    return null;
  }
  return data as unknown as FlightDeferralSummary;
}

export interface FlightBounceSummary {
  total_bounced: number;
  by_type: Record<string, number>; // e.g. { hard: 7, soft: 2, spam: 1 }
  top_reasons: { reason: string; count: number }[];
  top_providers: { provider: string; count: number }[];
}

/**
 * Aggregate of a flight's BOUNCED recipients — hard/soft/spam breakdown, top provider reasons, and
 * receiving-domain concentration. Powers the FlightDetail bounce panel (G4 bounce-source
 * attribution) so an operator can see WHY a send bounced. Aggregates only, never addresses.
 */
export async function getFlightBounceSummary(
  flightId: UUID,
): Promise<FlightBounceSummary | null> {
  const { data, error } = await supabase.rpc("get_flight_bounce_summary", {
    p_flight_id: flightId,
  });
  if (error) {
    console.error("Error fetching flight bounce summary:", error);
    return null;
  }
  return data as unknown as FlightBounceSummary;
}

export interface FlightRecipientTableProgress {
  total: number;
  pending: number;
  claimed: number;
  sent: number;
  failed: number;
  skipped: number;
  unconfirmed: number;
  latest_recipient_progress_at: string | null;
  latest_attempt_progress_at: string | null;
  latest_progress_at: string | null;
}

const RECIPIENT_TABLE_STATUSES = [
  "pending",
  "claimed",
  "sent",
  "failed",
  "skipped",
  "unconfirmed",
] as const;

type RecipientTableStatus = (typeof RECIPIENT_TABLE_STATUSES)[number];

async function countFlightRecipientsByStatus(
  flightId: UUID,
  status?: RecipientTableStatus,
): Promise<number> {
  let query = supabase
    .from("campaign_flight_send_recipients")
    .select("id", { count: "exact", head: true })
    .eq("flight_id", flightId);

  if (status) {
    query = query.eq("status", status);
  }

  const { count, error } = await query;
  if (error) {
    throw error;
  }
  return count ?? 0;
}

function maxIsoDate(values: Array<string | null | undefined>): string | null {
  let latest: string | null = null;
  let latestTime = Number.NEGATIVE_INFINITY;
  for (const value of values) {
    if (!value) {
      continue;
    }
    const time = new Date(value).getTime();
    if (!Number.isFinite(time)) {
      continue;
    }
    if (time > latestTime) {
      latestTime = time;
      latest = value;
    }
  }
  return latest;
}

/**
 * Recovery sub-flight row — narrow projection for the FlightDetail
 * "Recovered via N sub-flights" callout AND the aggregated header
 * stat cards (Recipients/Sent/Delivered/Opened/Clicked/Bounced).
 */
export interface FlightRecoverySplit {
  id: UUID;
  name: string;
  status: string;
  emails_sent: number | null;
  emails_delivered: number | null;
  emails_opened: number | null;
  emails_clicked: number | null;
  emails_bounced: number | null;
  emails_deferred: number | null;
  total_recipients: number | null;
  created_at: string;
}

/**
 * Fetch recovery sub-flights for a parent flight that was rescued
 * via the "manual_split_into_N_subflights" pattern (see
 * docs/AUDIT_PROMPT_FLIGHT_SEND_RECIPIENT_TABLE_AND_LEASES.md).
 *
 * Discovery is by name pattern: sub-flights are named
 * `<parent_name> — Split N/M`. We match the prefix and filter by
 * campaign_id so cross-campaign collisions don't pollute the list.
 *
 * Returns [] when the parent has no recovery splits — caller should
 * gate the callout on the parent's metadata flag, not the result of
 * this helper.
 */
export async function getFlightRecoverySplits(
  parentFlightId: UUID,
  parentCampaignId: UUID,
  parentName: string,
): Promise<FlightRecoverySplit[]> {
  // PostgREST .ilike() needs `%` wildcards; escape any underscore in
  // the parent name so we don't match unrelated names with a single
  // char between the prefix and " — Split".
  const escapedName = parentName.replace(/([%_])/g, "\\$1");
  const { data, error } = await supabase
    .from("campaign_flights")
    .select(
      "id, name, status, emails_sent, emails_delivered, emails_opened, emails_clicked, emails_bounced, emails_deferred, total_recipients, created_at",
    )
    .eq("campaign_id", parentCampaignId)
    .ilike("name", `${escapedName} — Split %`)
    .neq("id", parentFlightId)
    .order("name", { ascending: true });

  if (error) {
    console.error("Error fetching flight recovery splits:", error);
    return [];
  }

  return (data ?? []) as FlightRecoverySplit[];
}

/**
 * Aggregate progress for the recipient-table send path.
 *
 * The underlying recipient / attempt tables contain email-address PII, so this
 * helper only returns counts and latest timestamps. If RLS blocks a caller, the
 * FlightDetail page falls back to the legacy denormalized counters.
 */
export async function getFlightRecipientTableProgress(
  flightId: UUID,
): Promise<FlightRecipientTableProgress | null> {
  try {
    const [
      total,
      pending,
      claimed,
      sent,
      failed,
      skipped,
      unconfirmed,
      latestRecipient,
      latestAttempt,
    ] = await Promise.all([
      countFlightRecipientsByStatus(flightId),
      countFlightRecipientsByStatus(flightId, "pending"),
      countFlightRecipientsByStatus(flightId, "claimed"),
      countFlightRecipientsByStatus(flightId, "sent"),
      countFlightRecipientsByStatus(flightId, "failed"),
      countFlightRecipientsByStatus(flightId, "skipped"),
      countFlightRecipientsByStatus(flightId, "unconfirmed"),
      supabase
        .from("campaign_flight_send_recipients")
        .select("updated_at")
        .eq("flight_id", flightId)
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
      supabase
        .from("campaign_flight_send_attempts")
        .select("attempted_at, resolved_at")
        .eq("flight_id", flightId)
        .order("attempted_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
    ]);

    if (latestRecipient.error) {
      throw latestRecipient.error;
    }
    if (latestAttempt.error) {
      throw latestAttempt.error;
    }

    const latestRecipientProgressAt =
      (latestRecipient.data as { updated_at?: string | null } | null)
        ?.updated_at ?? null;
    const latestAttemptRow = latestAttempt.data as {
      attempted_at?: string | null;
      resolved_at?: string | null;
    } | null;
    const latestAttemptProgressAt = maxIsoDate([
      latestAttemptRow?.attempted_at,
      latestAttemptRow?.resolved_at,
    ]);

    return {
      total,
      pending,
      claimed,
      sent,
      failed,
      skipped,
      unconfirmed,
      latest_recipient_progress_at: latestRecipientProgressAt,
      latest_attempt_progress_at: latestAttemptProgressAt,
      latest_progress_at: maxIsoDate([
        latestRecipientProgressAt,
        latestAttemptProgressAt,
      ]),
    };
  } catch (error) {
    console.warn("Recipient-table flight progress unavailable:", error);
    return null;
  }
}

/**
 * Create a new flight
 */
export async function createFlight(
  flight: CreateFlightDTO,
): Promise<CampaignFlight> {
  const { data, error } = await supabase
    .from("campaign_flights")
    .insert([
      {
        ...flight,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
    ])
    .select()
    .single();

  if (error) {
    console.error("Error creating flight:", error);
    throw new Error(`Failed to create flight: ${error.message}`);
  }

  // Update campaign flight count
  await incrementCampaignFlightCount(flight.campaign_id);

  // Trigger voucher generation when the flight is set to issue vouchers
  // (vouchers derive from the linked offer; generation is idempotent per
  // flight+contact so re-triggers are safe).
  if (flight.generate_vouchers) {
    generateFlightVouchers(data.id).catch((err) => {
      console.error("Error triggering voucher generation:", err);
    });
  }

  return data as CampaignFlight;
}

/**
 * Duplicate a single flight within the same campaign. The copy is always an
 * unscheduled draft. Field mapping (copy / reset / inherit) lives in
 * utils/flightDuplicate.ts and mirrors public._duplicate_campaign_impl:
 *  - operator configuration is copied (incl. send_timezone, email category, IP pool);
 *  - send/run state is reset (metadata is an allowlist — the audience-exclusion
 *    breakdown frozen at the source's send, send stats, recovery notes etc. are dropped);
 *  - segment (owner decision 2026-10-15, option A): a copy keeps the original's segment,
 *    except a voucher flight (generate_vouchers), which starts with none (2026-06-29 fix);
 *  - a reminder stays a reminder of the same source flight and inherits that source's
 *    current segment, exactly as the flight form does on save (none if that segment is
 *    dynamic: a reminder may not sit on a dynamic segment); it never mints vouchers.
 *  - created_by = the signed-in user (the database trigger enforces the same).
 *  - Event in a Box: a copy of a flight in an EIB campaign starts with NO segment (owner
 *    decision 2026-10-15). EIB = the markers the EIB guards use: the flight (or any flight of its
 *    campaign) carries eib_launch_id, or a launch names the campaign (event_in_a_box_launches.campaign_id).
 * Branch / A/B graph references are NOT copied (no orphan edges within the campaign);
 * use duplicateCampaign() to preserve the full graph.
 */
/**
 * Event in a Box campaign? Same markers as migration 488 / the EIB guards: the flight itself or any
 * flight of the campaign carries eib_launch_id, or a launch names the campaign. Fails closed: a
 * lookup error is thrown (the copy is not created with a possibly-wrong segment).
 */
async function isEibCampaign(
  campaignId: UUID,
  flightEibLaunchId: string | null,
): Promise<boolean> {
  if (flightEibLaunchId) {
    return true;
  }
  const [flights, launches] = await Promise.all([
    supabase
      .from("campaign_flights")
      .select("id")
      .eq("campaign_id", campaignId)
      .not("eib_launch_id", "is", null)
      .limit(1),
    supabase
      .from("event_in_a_box_launches")
      .select("id")
      .eq("campaign_id", campaignId)
      .limit(1),
  ]);
  if (flights.error || launches.error) {
    throw new Error(
      `Failed to check whether this is an Event in a Box campaign: ${
        (flights.error ?? launches.error)!.message
      }`,
    );
  }
  return (flights.data?.length ?? 0) > 0 || (launches.data?.length ?? 0) > 0;
}

export async function duplicateFlight(flightId: UUID): Promise<CampaignFlight> {
  const source = await getFlight(flightId);
  if (!source) {
    throw new Error("Source flight not found");
  }

  let reminderSourceListId: string | null = null;
  if (source.voucher_source_flight_id) {
    const { data: voucherSource, error } = await supabase
      .from("campaign_flights")
      .select("id, list_id, voucher_source_flight_id, list:lists(is_dynamic)")
      .eq("id", source.voucher_source_flight_id)
      .maybeSingle();
    if (error) {
      throw new Error(
        `Failed to load the reminder's source flight: ${error.message}`,
      );
    }
    if (!voucherSource) {
      throw new Error("The reminder's source flight no longer exists");
    }
    if (voucherSource.voucher_source_flight_id) {
      // Same invariant send-campaign-flight enforces: no reminder chains.
      throw new Error(
        "The reminder's source flight is itself a reminder; fix the source before duplicating",
      );
    }
    // A reminder may not sit on a dynamic segment (prevent_dynamic_voucher_flight_save would
    // refuse the insert): inherit nothing in that case, mirroring migration 487.
    const sourceList = (
      voucherSource as { list?: { is_dynamic?: boolean | null } | null }
    ).list;
    reminderSourceListId =
      sourceList?.is_dynamic === true
        ? null
        : ((voucherSource.list_id as string | null) ?? null);
  }

  const eibCampaign = await isEibCampaign(
    source.campaign_id,
    (source as { eib_launch_id?: string | null }).eib_launch_id ?? null,
  );

  // created_by = the user making the copy (owner decision 2026-10-14), from the session.
  const { data: sessionData } = await supabase.auth.getSession();
  const createdBy = sessionData?.session?.user?.id ?? null;

  return await createFlight(
    buildDuplicateFlightDTO(source, reminderSourceListId, createdBy, {
      eibCampaign,
    }) as CreateFlightDTO,
  );
}

/**
 * Duplicate an entire campaign, including all flights. Branch and
 * parent references between flights in the source campaign are
 * remapped to point at the cloned flights via a SECURITY DEFINER
 * RPC (atomic in a single transaction). Send history, recipient
 * snapshots, vouchers, and email tracking are NEVER cloned.
 *
 * Returns the new campaign's id.
 */
export async function duplicateCampaign(campaignId: UUID): Promise<UUID> {
  const { data, error } = await supabase.rpc("duplicate_campaign", {
    p_source_campaign_id: campaignId,
  });
  if (error) {
    throw error;
  }
  if (!data) {
    throw new Error("duplicate_campaign returned no id");
  }
  return data as UUID;
}

/**
 * Update a flight
 */
export async function updateFlight(
  id: UUID,
  updates: UpdateFlightDTO,
): Promise<CampaignFlight> {
  const { data, error } = await supabase
    .from("campaign_flights")
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
    })
    .eq("id", id)
    .select()
    .single();

  if (error) {
    console.error("Error updating flight:", error);
    throw new Error(`Failed to update flight: ${error.message}`);
  }

  // Allow enabling vouchers on an existing flight. Generation is idempotent
  // (skips contacts that already have a voucher for this flight), so it's safe
  // to (re)trigger whenever the flight is set to issue vouchers.
  // Also re-trigger when the TARGET LIST changes on a voucher-enabled flight,
  // even if the flag itself wasn't in this update payload: generation is the
  // convergence point that mints for the new list AND prunes artifacts left
  // over from the old one (list-swap reconciliation in generate-flight-vouchers).
  const updatedFlight = data as CampaignFlight;
  if (
    updates.generate_vouchers ||
    (updates.list_id !== undefined && updatedFlight.generate_vouchers)
  ) {
    generateFlightVouchers(id).catch((err) => {
      console.error("Error triggering voucher generation on update:", err);
    });
  }

  return data as CampaignFlight;
}

/**
 * Delete a flight
 */
export async function deleteFlight(id: UUID): Promise<void> {
  // Get flight to know which campaign to update
  const flight = await getFlight(id);

  const { data: deleted, error } = await supabase
    .from("campaign_flights")
    .delete()
    .eq("id", id)
    .select("id");

  if (error) {
    console.error("Error deleting flight:", error);
    throw new Error(`Failed to delete flight: ${error.message}`);
  }

  // Verify the row was actually deleted (RLS can silently block deletes)
  if (!deleted || deleted.length === 0) {
    // Row wasn't deleted — check if it still exists
    const stillExists = await getFlight(id);
    if (stillExists) {
      throw new Error("Failed to delete flight: permission denied");
    }
  }

  // Update campaign flight count
  if (flight) {
    await decrementCampaignFlightCount(flight.campaign_id);
  }
}

/**
 * Schedule a flight for sending
 */
export async function scheduleFlight(
  flightId: UUID,
  scheduledSendAt: string,
): Promise<CampaignFlight> {
  return updateFlight(flightId, {
    scheduled_send_at: scheduledSendAt,
    status: "scheduled",
  });
}

/**
 * Mark flight as sent
 */
export async function markFlightAsSent(
  flightId: UUID,
): Promise<CampaignFlight> {
  return updateFlight(flightId, {
    sent_at: new Date().toISOString(),
    status: "sent",
  });
}

// ========================================
// SMS TEMPLATE OPERATIONS
// ========================================

/**
 * Get all SMS templates
 */
export async function getSMSTemplates(): Promise<SMSTemplate[]> {
  // ⚠️ RPC, not a table select — see getContactSmsHistory. The 2026-09-24-020 lockdown removed the
  // authenticated grant on sms_templates, so the direct query silently returned nothing.
  const { data, error } = await supabase.rpc("sms_list_templates");

  if (error) {
    console.error("Error fetching SMS templates:", error);
    throw new Error(`Failed to fetch SMS templates: ${error.message}`);
  }

  return (data ?? []) as unknown as SMSTemplate[];
}

/**
 * Get a single SMS template by ID
 */
export async function getSMSTemplate(id: UUID): Promise<SMSTemplate | null> {
  // Same reason as above: read through the definer RPC and pick the row here.
  const { data: rows, error } = await supabase.rpc("sms_list_templates");
  const data =
    ((rows ?? []) as unknown as SMSTemplate[]).find((t) => t.id === id) ?? null;

  if (error) {
    if (error.code === "PGRST116") {
      return null; // Not found
    }
    console.error("Error fetching SMS template:", error);
    throw new Error(`Failed to fetch SMS template: ${error.message}`);
  }

  return data as SMSTemplate;
}

/**
 * Create a new SMS template
 */
export async function createSMSTemplate(
  template: CreateSMSTemplateDTO,
): Promise<SMSTemplate> {
  // Extract variables from template body
  const variables = extractVariablesFromTemplate(template.body);

  const { data, error } = await supabase
    .from("sms_templates")
    .insert([
      {
        ...template,
        variables: template.variables || variables,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
    ])
    .select()
    .single();

  if (error) {
    console.error("Error creating SMS template:", error);
    throw new Error(`Failed to create SMS template: ${error.message}`);
  }

  return data as SMSTemplate;
}

/**
 * Update an SMS template
 */
export async function updateSMSTemplate(
  id: UUID,
  updates: UpdateSMSTemplateDTO,
): Promise<SMSTemplate> {
  // If body is being updated, re-extract variables
  let variables = updates.variables;
  if (updates.body && !updates.variables) {
    variables = extractVariablesFromTemplate(updates.body);
  }

  const { data, error } = await supabase
    .from("sms_templates")
    .update({
      ...updates,
      variables: variables || updates.variables,
      updated_at: new Date().toISOString(),
    })
    .eq("id", id)
    .is("deleted_at", null)
    .select()
    .single();

  if (error) {
    console.error("Error updating SMS template:", error);
    throw new Error(`Failed to update SMS template: ${error.message}`);
  }

  return data as SMSTemplate;
}

/**
 * Soft delete an SMS template
 */
export async function deleteSMSTemplate(id: UUID): Promise<void> {
  const { error } = await supabase
    .from("sms_templates")
    .update({ deleted_at: new Date().toISOString() })
    .eq("id", id);

  if (error) {
    console.error("Error deleting SMS template:", error);
    throw new Error(`Failed to delete SMS template: ${error.message}`);
  }
}

// ========================================
// VOUCHER GENERATION
// ========================================

/**
 * Generate vouchers for all contacts in a flight
 * This calls the generate-flight-vouchers edge function
 */
export async function generateFlightVouchers(flightId: UUID): Promise<void> {
  const { data: session } = await supabase.auth.getSession();

  if (!session?.session) {
    throw new Error("No active session");
  }

  const { data, error } = await supabase.functions.invoke(
    "generate-flight-vouchers",
    {
      body: { flight_id: flightId },
      headers: {
        Authorization: `Bearer ${session.session.access_token}`,
      },
    },
  );

  if (error) {
    console.error("Error generating flight vouchers:", error);
    throw new Error(`Failed to generate vouchers: ${error.message}`);
  }

  console.log("Voucher generation response:", data);
}

// ========================================
// HELPER FUNCTIONS
// ========================================

/**
 * Extract variable placeholders from SMS template body
 */
function extractVariablesFromTemplate(body: string): string[] {
  const matches = body.match(/{([^}]+)}/g);
  if (!matches) {
    return [];
  }

  // Remove braces and return unique variables
  const variables = matches.map((match) => match.replace(/[{}]/g, ""));
  return Array.from(new Set(variables));
}

/**
 * Increment campaign's total_flights counter
 */
async function incrementCampaignFlightCount(campaignId: UUID): Promise<void> {
  const { data: campaign } = await supabase
    .from("campaigns")
    .select("total_flights")
    .eq("id", campaignId)
    .single();

  if (campaign) {
    await supabase
      .from("campaigns")
      .update({ total_flights: (campaign.total_flights || 0) + 1 })
      .eq("id", campaignId);
  }
}

/**
 * Decrement campaign's total_flights counter
 */
async function decrementCampaignFlightCount(campaignId: UUID): Promise<void> {
  const { data: campaign } = await supabase
    .from("campaigns")
    .select("total_flights")
    .eq("id", campaignId)
    .single();

  if (campaign && campaign.total_flights > 0) {
    await supabase
      .from("campaigns")
      .update({ total_flights: campaign.total_flights - 1 })
      .eq("id", campaignId);
  }
}

/**
 * Validate SMS template
 */
export function validateSMSTemplate(body: string): {
  valid: boolean;
  errors: string[];
  warnings: string[];
  characterCount: number;
  segmentCount: number;
} {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Check minimum length
  if (body.trim().length < 10) {
    errors.push("Message body must be at least 10 characters");
  }

  // Check maximum length (160 chars per segment, max 10 segments = 1600 chars)
  if (body.length > 1600) {
    errors.push("Message body exceeds maximum length of 1600 characters");
  }

  // Calculate SMS segments (160 chars per segment for GSM-7, 70 for Unicode)
  // eslint-disable-next-line no-control-regex
  const hasUnicode = /[^\x00-\x7F]/.test(body);
  const charsPerSegment = hasUnicode ? 70 : 160;
  const segmentCount = Math.ceil(body.length / charsPerSegment);

  // Warn if multi-segment
  if (segmentCount > 1) {
    warnings.push(`Message will be sent as ${segmentCount} SMS segments`);
  }

  // Warn if approaching limit
  if (segmentCount > 5) {
    warnings.push("Message is very long and may be expensive to send");
  }

  // Check for unmatched braces
  const openBraces = (body.match(/{/g) || []).length;
  const closeBraces = (body.match(/}/g) || []).length;
  if (openBraces !== closeBraces) {
    errors.push("Unmatched braces in variable placeholders");
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
    characterCount: body.length,
    segmentCount,
  };
}

/**
 * Search campaigns
 */
export async function searchCampaigns(
  query: string,
): Promise<CampaignListRow[]> {
  // Strip the .or() grammar characters first: a comma or parenthesis in the
  // term (e.g. "Range Night (Oct)") used to break the filter and error.
  const safe = sanitizePostgrestSearchTerm(query);
  if (!safe) {
    return getCampaigns();
  }
  const { data, error } = await supabase
    .from("campaigns")
    .select("*, campaign_flights(status)")
    .not("is_deleted", "is", true)
    .or(`name.ilike.*${safe}*,description.ilike.*${safe}*`)
    .order("created_at", { ascending: false })
    .limit(20);

  if (error) {
    console.error("Error searching campaigns:", error);
    throw new Error(`Failed to search campaigns: ${error.message}`);
  }

  return toCampaignListRows(data);
}

/**
 * Campaign Summary Export — one row per flight, point-in-time
 * engagement snapshot. Powers the "Summary Export" button on
 * CampaignDetail. RPC defined in migration
 * 2026-06-04-001-campaign-summary-export-rpc.sql.
 *
 * Rate denominator is `sent` (matches existing CampaignDetail UI).
 */
export interface CampaignSummaryRow {
  flight_id: UUID;
  flight_name: string;
  status: string;
  ab_test_variant: string | null;
  scheduled_send_at: string | null;
  sent_at: string | null;
  recipients: number;
  sent: number;
  delivered: number;
  opened_unique: number;
  clicked_unique: number;
  bounced: number;
  unsubscribed: number;
  spam: number;
  pending: number;
  open_rate_pct: number;
  click_rate_pct: number;
}

export async function getCampaignSummaryExport(
  campaignId: UUID,
): Promise<CampaignSummaryRow[]> {
  const { data, error } = await supabase.rpc("campaign_summary_export", {
    p_campaign_id: campaignId,
  });
  if (error) {
    console.error("Error fetching campaign summary export:", error);
    throw new Error(`Failed to fetch campaign summary: ${error.message}`);
  }
  return (data ?? []) as CampaignSummaryRow[];
}

// ========================================
// AUTOMATIC NEVERBOUNCE CHECK (migration 2026-10-08-393)
// ========================================
// The manual "validate with NeverBounce" panel and its prepare/status/confirm calls were retired
// 2026-10-08: every flight email is now checked automatically before it is sent. These are the
// read-only status for the flight page and the Event in a Box exclude-risky option.

export interface FlightEmailCheckStatus {
  /** The check is on for THIS flight: the server switch (system_config.neverbounce_presend_enabled) AND the
   *  flight is in scope (neverbounce_presend_flight_allowlist, migration 397). Any "checking" message must
   *  require this to be true. */
  enabled: boolean;
  /** not_started: the send has not reached the check yet; scanning/waiting: the send is parked
   *  while addresses are checked; passed: every address had a fresh result. */
  state: "not_started" | "scanning" | "waiting" | "passed";
  /** Distinct addresses the send is waiting on. */
  waiting_addresses: number;
  /** Of those, how many are in a NeverBounce job right now. */
  being_checked: number;
  waiting_since: string | null;
  /** Recipients skipped because their address had no result within 24 hours. */
  no_result_skipped: number;
  passed_at: string | null;
}

/** Read-only: where this flight is in the automatic NeverBounce check. */
export async function getFlightEmailCheckStatus(
  flightId: UUID,
): Promise<FlightEmailCheckStatus> {
  const { data, error } = await supabase.rpc("flight_email_check_status", {
    p_flight_id: flightId,
  });
  if (error) {
    throw new Error(error.message);
  }
  return data as FlightEmailCheckStatus;
}

/** Event in a Box: turn the "exclude risky" option on or off for this launch's invitation emails
 *  that have not started sending (Event in a Box flights cannot be edited directly). */
export async function setEibExcludeRisky(
  flightId: UUID,
  exclude: boolean,
): Promise<{ ok: boolean; exclude_risky: boolean; flights_updated: string[] }> {
  const { data, error } = await supabase.rpc("eib_set_exclude_risky", {
    p_flight_id: flightId,
    p_exclude: exclude,
  });
  if (error) {
    throw new Error(error.message);
  }
  return data as {
    ok: boolean;
    exclude_risky: boolean;
    flights_updated: string[];
  };
}

// ============================================================================
// Flight-level unsubscribe CSV export (2026-08-20)
//
// Recipient-level PII download served ONLY by the JWT + role-gated
// `export-flight-unsubscribes` edge function. The browser never fetches raw
// addresses to build the file — it downloads the finished CSV from the
// authorized server endpoint. `count_only` powers the zero-state / metric
// without exposing any address.
// ============================================================================

export interface FlightUnsubscribeSummary {
  operational_rows: number;
  distinct_recipients: number;
  total_events: number;
  as_of: string;
  recovery_children_included: boolean;
  asm_history_coverage_note: string;
}

async function callUnsubscribeExport(
  flightId: UUID,
  countOnly: boolean,
): Promise<Response> {
  const { data: sessionData, error: sessionError } =
    await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;
  if (sessionError || !accessToken) {
    throw sessionError ?? new Error("An authenticated session is required");
  }
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
  return fetch(`${supabaseUrl}/functions/v1/export-flight-unsubscribes`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      apikey: anonKey,
      "Content-Type": "application/json",
      "x-application-name": "uslawshield-crm",
    },
    body: JSON.stringify({ flight_id: flightId, count_only: countOnly }),
  });
}

async function unsubscribeErrorMessage(
  response: Response,
  fallback: string,
): Promise<string> {
  try {
    const body = (await response.json()) as { error?: string };
    if (body.error) {
      return body.error;
    }
  } catch {
    // keep fallback for non-JSON gateway responses
  }
  return fallback;
}

/**
 * Fetch the flight's unsubscribe counts WITHOUT any recipient address. Used to
 * decide the button's enabled/zero-state and show the metric.
 */
export async function getFlightUnsubscribeSummary(
  flightId: UUID,
): Promise<FlightUnsubscribeSummary> {
  const response = await callUnsubscribeExport(flightId, true);
  if (!response.ok) {
    throw new Error(
      await unsubscribeErrorMessage(
        response,
        `Unsubscribe summary failed (${response.status})`,
      ),
    );
  }
  return (await response.json()) as FlightUnsubscribeSummary;
}

/**
 * Download the flight's unsubscribe CSV from the authorized server endpoint and
 * trigger a browser save. Returns the row/recipient counts for a success toast.
 */
export async function downloadFlightUnsubscribesCsv(
  flightId: UUID,
): Promise<{ rowCount: number; distinctRecipients: number; filename: string }> {
  const response = await callUnsubscribeExport(flightId, false);
  if (!response.ok) {
    throw new Error(
      await unsubscribeErrorMessage(
        response,
        `Unsubscribe export failed (${response.status})`,
      ),
    );
  }

  const csv = await response.text();
  const rowCount = Number(response.headers.get("X-Row-Count")) || 0;
  const distinctRecipients =
    Number(response.headers.get("X-Distinct-Recipients")) || 0;
  const filename =
    response.headers.get("X-Filename") || `unsubscribes-${flightId}.csv`;

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);

  return { rowCount, distinctRecipients, filename };
}

// ============================================================================
// Flight-level OPENER CSV export (2026-09-10)
//
// Same model as the unsubscribe export above, deliberately: recipient-level PII
// served ONLY by the JWT + management-role gated `export-flight-openers` edge
// function, the browser never queries email_tracking to build the file (its RLS
// is `SELECT TO authenticated USING (true)`, so a client-side build would expose
// addresses to any logged-in user), and `count_only` powers the zero-state and
// the human/machine metric without exposing any address.
// ============================================================================

export interface FlightOpenerSummary {
  /** Deduplicated people — a contact who opened five times counts once. */
  people: number;
  /** Underlying email_tracking rows with opened_at set, before dedup. */
  open_rows: number;
  /** SendGrid said a person opened (sg_machine_open=false). */
  human: number;
  /** SendGrid said a machine opened (Apple MPP / security scanner). */
  machine: number;
  /** No provider verdict retained — NOT evidence of a human. */
  unclassified: number;
  as_of: string;
  coverage_note: string;
}

async function callOpenerExport(
  flightId: UUID,
  countOnly: boolean,
): Promise<Response> {
  const { data: sessionData, error: sessionError } =
    await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;
  if (sessionError || !accessToken) {
    throw sessionError ?? new Error("An authenticated session is required");
  }
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
  return fetch(`${supabaseUrl}/functions/v1/export-flight-openers`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      apikey: anonKey,
      "Content-Type": "application/json",
      "x-application-name": "uslawshield-crm",
    },
    body: JSON.stringify({ flight_id: flightId, count_only: countOnly }),
  });
}

/**
 * Fetch the flight's opener counts WITHOUT any recipient address. Drives the
 * button's enabled/zero-state and the human/machine breakdown.
 */
export async function getFlightOpenerSummary(
  flightId: UUID,
): Promise<FlightOpenerSummary> {
  const response = await callOpenerExport(flightId, true);
  if (!response.ok) {
    throw new Error(
      await unsubscribeErrorMessage(
        response,
        `Opener summary failed (${response.status})`,
      ),
    );
  }
  return (await response.json()) as FlightOpenerSummary;
}

/**
 * Download the flight's opener CSV from the authorized server endpoint and
 * trigger a browser save. Returns the counts for a success message.
 */
export async function downloadFlightOpenersCsv(flightId: UUID): Promise<{
  rowCount: number;
  human: number;
  machine: number;
  unclassified: number;
  filename: string;
}> {
  const response = await callOpenerExport(flightId, false);
  if (!response.ok) {
    throw new Error(
      await unsubscribeErrorMessage(
        response,
        `Opener export failed (${response.status})`,
      ),
    );
  }

  const csv = await response.text();
  const rowCount = Number(response.headers.get("X-Row-Count")) || 0;
  const human = Number(response.headers.get("X-Human-Count")) || 0;
  const machine = Number(response.headers.get("X-Machine-Count")) || 0;
  const unclassified =
    Number(response.headers.get("X-Unclassified-Count")) || 0;
  const filename =
    response.headers.get("X-Filename") || `openers-${flightId}.csv`;

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);

  return { rowCount, human, machine, unclassified, filename };
}

// ============================================================================
// Flight-level NON-OPENER CSV export (2026-09-17)
//
// Same secured server-download model as the opener export. A non-opener is a
// send-evidenced recipient with no recorded open of any kind at the export's
// immutable as-of boundary. Machine/scanner opens count as opens.
// ============================================================================

export interface FlightNonOpenerSummary {
  people: number;
  as_of: string;
  definition_note: string;
}

async function callNonOpenerExport(
  flightId: UUID,
  countOnly: boolean,
): Promise<Response> {
  const { data: sessionData, error: sessionError } =
    await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;
  if (sessionError || !accessToken) {
    throw sessionError ?? new Error("An authenticated session is required");
  }
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
  return fetch(`${supabaseUrl}/functions/v1/export-flight-nonopeners`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      apikey: anonKey,
      "Content-Type": "application/json",
      "x-application-name": "uslawshield-crm",
    },
    body: JSON.stringify({ flight_id: flightId, count_only: countOnly }),
  });
}

export async function getFlightNonOpenerSummary(
  flightId: UUID,
): Promise<FlightNonOpenerSummary> {
  const response = await callNonOpenerExport(flightId, true);
  if (!response.ok) {
    throw new Error(
      await unsubscribeErrorMessage(
        response,
        `Non-opener summary failed (${response.status})`,
      ),
    );
  }
  return (await response.json()) as FlightNonOpenerSummary;
}

export async function downloadFlightNonOpenersCsv(
  flightId: UUID,
): Promise<{ rowCount: number; filename: string }> {
  const response = await callNonOpenerExport(flightId, false);
  if (!response.ok) {
    throw new Error(
      await unsubscribeErrorMessage(
        response,
        `Non-opener export failed (${response.status})`,
      ),
    );
  }

  const csv = await response.text();
  const rowCount = Number(response.headers.get("X-Row-Count")) || 0;
  const filename =
    response.headers.get("X-Filename") || `non-openers-${flightId}.csv`;

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);

  return { rowCount, filename };
}
